import { Die } from './types';

// ============================================================
// 统一计分引擎 — 全项目唯一的 Farkle 计分逻辑
// ============================================================

/**
 * 检查选中的骰子是否构成有效组合
 */
export const hasValidSelection = (dice: Die[]): boolean => {
  const selectedDice = dice.filter(die => die.selected && !die.locked);

  if (selectedDice.length === 0) return false;

  let validDiceCount = 0;

  const values = selectedDice.map(die => die.value).sort((a, b) => a - b);

  // 全顺 123456
  if (values.length === 6 && JSON.stringify(values) === JSON.stringify([1, 2, 3, 4, 5, 6])) {
    return true;
  }

  // 顺子 12345 或 23456 + 剩余的1/5
  if (values.length >= 5) {
    const has12345 = [1, 2, 3, 4, 5].every(v => values.includes(v));
    if (has12345) {
      const remainingValues = values.filter(v => {
        return (v !== 1 && v !== 2 && v !== 3 && v !== 4 && v !== 5) ||
               (v === 1 && values.filter(x => x === 1).length > 1) ||
               (v === 2 && values.filter(x => x === 2).length > 1) ||
               (v === 3 && values.filter(x => x === 3).length > 1) ||
               (v === 4 && values.filter(x => x === 4).length > 1) ||
               (v === 5 && values.filter(x => x === 5).length > 1);
      });
      if (remainingValues.every(v => v === 1 || v === 5)) {
        return true;
      }
    }

    const has23456 = [2, 3, 4, 5, 6].every(v => values.includes(v));
    if (has23456) {
      const remainingValues = values.filter(v => {
        return (v !== 2 && v !== 3 && v !== 4 && v !== 5 && v !== 6) ||
               (v === 2 && values.filter(x => x === 2).length > 1) ||
               (v === 3 && values.filter(x => x === 3).length > 1) ||
               (v === 4 && values.filter(x => x === 4).length > 1) ||
               (v === 5 && values.filter(x => x === 5).length > 1) ||
               (v === 6 && values.filter(x => x === 6).length > 1);
      });
      if (remainingValues.every(v => v === 1 || v === 5)) {
        return true;
      }
    }
  }

  // 统计每个点数的数量
  const counts = new Map<number, number>();
  selectedDice.forEach(die => {
    counts.set(die.value, (counts.get(die.value) || 0) + 1);
  });

  Array.from(counts.entries()).forEach(([value, count]) => {
    if (count >= 3) {
      validDiceCount += count;
    } else {
      if (value === 1 || value === 5) {
        validDiceCount += count;
      }
    }
  });

  return validDiceCount === selectedDice.length;
};

/**
 * 计算选中骰子的分数
 */
export const calculateSelectionScore = (dice: Die[]): number => {
  const selectedDice = dice.filter(die => die.selected && !die.locked);

  if (!hasValidSelection(dice)) {
    return 0;
  }

  const values = selectedDice.map(die => die.value).sort((a, b) => a - b);

  // 全顺 123456
  if (values.length === 6 && JSON.stringify(values) === JSON.stringify([1, 2, 3, 4, 5, 6])) {
    return 1500;
  }

  // 顺子 12345 或 23456 + 剩余的1/5
  if (values.length >= 5) {
    const has12345 = [1, 2, 3, 4, 5].every(v => values.includes(v));
    if (has12345) {
      let remainingScore = 0;
      const counts: Record<number, number> = {};
      values.forEach(v => { counts[v] = (counts[v] || 0) + 1; });
      if (counts[1] > 1) remainingScore += (counts[1] - 1) * 100;
      if (counts[5] > 1) remainingScore += (counts[5] - 1) * 50;
      return 500 + remainingScore;
    }

    const has23456 = [2, 3, 4, 5, 6].every(v => values.includes(v));
    if (has23456) {
      let remainingScore = 0;
      const counts: Record<number, number> = {};
      values.forEach(v => { counts[v] = (counts[v] || 0) + 1; });
      if (counts[1]) remainingScore += counts[1] * 100;
      if (counts[5] > 1) remainingScore += (counts[5] - 1) * 50;
      return 750 + remainingScore;
    }
  }

  // 连子 & 散骰
  const counts = new Map<number, number>();
  selectedDice.forEach(die => {
    counts.set(die.value, (counts.get(die.value) || 0) + 1);
  });

  let score = 0;

  Array.from(counts.entries()).forEach(([value, count]) => {
    if (count >= 3) {
      const baseScore = value === 1 ? 1000 : value * 100;
      const multiplier = Math.pow(2, count - 3);
      score += baseScore * multiplier;
    } else if (value === 1) {
      score += count * 100;
    } else if (value === 5) {
      score += count * 50;
    }
  });

  return score;
};

/**
 * 检查未锁定的骰子中是否存在任何可得分的组合
 * 统一了 Game.tsx 中原来的 checkForPossibleScoring 和 checkAllPossibleSelections
 */
export const hasPossibleScoring = (dice: Die[]): boolean => {
  const unlockedDice = dice.filter(die => !die.locked);

  if (unlockedDice.length === 0) return false;

  // 1. 有1或5
  if (unlockedDice.some(die => die.value === 1 || die.value === 5)) {
    return true;
  }

  // 2. 统计点数
  const counts = new Array(7).fill(0);
  unlockedDice.forEach(die => counts[die.value]++);

  // 3. 三个或更多相同
  for (let i = 1; i <= 6; i++) {
    if (counts[i] >= 3) return true;
  }

  // 4. 顺子（5颗）
  if (unlockedDice.length >= 5) {
    if (counts[1] >= 1 && counts[2] >= 1 && counts[3] >= 1 && counts[4] >= 1 && counts[5] >= 1) {
      return true;
    }
    if (counts[2] >= 1 && counts[3] >= 1 && counts[4] >= 1 && counts[5] >= 1 && counts[6] >= 1) {
      return true;
    }
  }

  // 5. 全顺 & 三对（6颗）
  if (unlockedDice.length === 6) {
    const values = unlockedDice.map(die => die.value).sort((a, b) => a - b);
    if (JSON.stringify(values) === JSON.stringify([1, 2, 3, 4, 5, 6])) {
      return true;
    }
    const pairCount = Object.values(
      unlockedDice.reduce<Record<number, number>>((acc, die) => {
        acc[die.value] = (acc[die.value] || 0) + 1;
        return acc;
      }, {})
    ).filter(c => c === 2).length;
    if (pairCount === 3) return true;
  }

  return false;
};

/**
 * 检查是否为 Farkle（未锁定骰子中没有任何可得分组合）
 */
export const isFarkle = (dice: Die[]): boolean => {
  return !hasPossibleScoring(dice);
};
